const PENDING_LOGIN_KEY = 'gtm_pending_login';
const PENDING_LOGIN_TTL_MS = 30 * 60 * 1000;

const GTM_USER_ID_KEY = 'gtm_user_id';
const PENDING_APPLICATION_KEY = 'gtm_pending_application';

export type GtmLoginMethod = 'email' | 'google';
export type GtmUserType = 'existing' | 'new';

export interface IPendingLoginEvent {
  expiresAt: number;
  method: GtmLoginMethod;
  userType?: GtmUserType;
}

export interface IPendingApplicationEvent {
  positionId: string | null;
}

const pushToDataLayer = (payload: Record<string, unknown>): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  if (process.env.REACT_APP_ENV !== 'production') {
    console.log('[dataLayer]', payload);
  }
};

export const pushUserIdEvent = (
  userId: string,
  positionId: string | null,
): void => {
  try {
    localStorage.setItem(GTM_USER_ID_KEY, userId);
  } catch {
    // localStorage may be unavailable in some browsers
  }
  pushToDataLayer({
    user_id: userId,
    position_id: positionId,
  });
};

export const pushLoginEvent = (
  method: GtmLoginMethod,
  userType: GtmUserType,
  positionId: string | null,
): void => {
  pushToDataLayer({
    event: 'login',
    method,
    user_type: userType,
    position_id: positionId,
  });
};

export const pushSubmitProfileEvent = (positionId: string | null): void => {
  pushToDataLayer({
    event: 'submit_profile',
    position_id: positionId,
  });
};

export const pushApplicationEvent = (
  isRejected: boolean,
  positionId: string | null,
): void => {
  pushToDataLayer({
    event: 'application',
    is_rejected: isRejected,
    position_id: positionId,
  });
};

export const setPendingLoginEvent = (
  method: GtmLoginMethod,
  userType?: GtmUserType,
): void => {
  const entry: IPendingLoginEvent = {
    expiresAt: Date.now() + PENDING_LOGIN_TTL_MS,
    method,
    userType,
  };

  localStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(entry));
};

export const consumePendingLoginEvent = (): IPendingLoginEvent | null => {
  const raw = localStorage.getItem(PENDING_LOGIN_KEY);

  if (!raw) return null;

  try {
    const entry: IPendingLoginEvent = JSON.parse(raw);

    localStorage.removeItem(PENDING_LOGIN_KEY);

    if (Date.now() > entry.expiresAt) return null;

    return entry;
  } catch {
    localStorage.removeItem(PENDING_LOGIN_KEY);
    return null;
  }
};

export const setPendingApplicationEvent = (positionId: string | null): void => {
  const entry: IPendingApplicationEvent = { positionId };
  localStorage.setItem(PENDING_APPLICATION_KEY, JSON.stringify(entry));
};

export const consumePendingApplicationEvent =
  (): IPendingApplicationEvent | null => {
    const raw = localStorage.getItem(PENDING_APPLICATION_KEY);

    if (!raw) return null;

    try {
      const entry: IPendingApplicationEvent = JSON.parse(raw);
      localStorage.removeItem(PENDING_APPLICATION_KEY);
      return entry;
    } catch {
      localStorage.removeItem(PENDING_APPLICATION_KEY);
      return null;
    }
  };
